import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MessagesSquare, Receipt, ShoppingBag } from 'lucide-react';
import * as api from '@/api/supervision.api';
import type { Periodo, Venta } from '@/api/tienda.api';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Comprobante } from '@/components/tienda/Comprobante';
import { dinero, numero, fecha } from '@/lib/formato-tienda';

/**
 * MIS VENTAS — lo que pasó la asesora, a la vista de ella misma.
 *
 * Hasta hoy la asesora pasaba la venta desde el chat y no tenía dónde volver a verla: para saber
 * cuánto llevaba en el día tenía que preguntarle a quien supervisa. Aquí están las suyas (nunca
 * las de otra), con su total y con las que se le quedaron en borrador por falta de existencia.
 * No trae costos: el margen es de la administración.
 */

const PERIODOS: Array<{ id: Periodo; etiqueta: string }> = [
  { id: 'hoy', etiqueta: 'Hoy' },
  { id: 'semana', etiqueta: 'Esta semana' },
  { id: 'mes', etiqueta: 'Este mes' },
];

const ESTADO: Record<Venta['estado'], { texto: string; tono: 'success' | 'warning' | 'danger' }> = {
  CONFIRMADA: { texto: 'Confirmada', tono: 'success' },
  BORRADOR: { texto: 'En borrador', tono: 'warning' },
  ANULADA: { texto: 'Anulada', tono: 'danger' },
};

export default function MisVentas() {
  // SUPERFILTRO (Josué, 14-oct): el período (antes pestañas; «Hoy» es lo de por defecto) y el
  // estado de la venta, en la URL. El período también manda en las cifras de arriba.
  const { filtros, poner, ponerVarios } = useFiltrosEnUrl(
    { periodo: 'hoy', estado: '', buscar: '' },
    { periodo: PERIODOS.map((p) => p.id), estado: Object.keys(ESTADO) },
  );
  const periodo = filtros.periodo as Periodo;
  const [comprobante, setComprobante] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['mis-ventas', periodo],
    queryFn: () => api.misVentas(periodo),
    refetchInterval: 60_000,
  });
  const todas = data?.ventas ?? [];
  const r = data?.resumen;
  const q = filtros.buscar.trim().toLowerCase();
  const ventas = todas.filter(
    (v) =>
      (!filtros.estado || v.estado === filtros.estado) &&
      (!q ||
        `${v.numero} ${v.cliente?.nombre ?? ''} ${v.lineas.map((l) => l.productVariant.product.name).join(' ')}`
          .toLowerCase()
          .includes(q)),
  );
  const facetas: Faceta[] = [
    { clave: 'periodo', titulo: 'Período', todas: 'Hoy', opciones: PERIODOS.filter((p) => p.id !== 'hoy').map((p) => ({ valor: p.id, etiqueta: p.etiqueta })) },
    {
      clave: 'estado',
      titulo: 'Estado',
      opciones: (Object.keys(ESTADO) as Array<Venta['estado']>).map((e) => ({ valor: e, etiqueta: ESTADO[e].texto, cuenta: todas.filter((v) => v.estado === e).length })),
    },
  ];

  return (
    <div data-mis-ventas className="space-y-5">
      <ModuleHeader
        titulo="Mis ventas"
        subtitulo="Las ventas que pasaste desde tus chats. Solo las tuyas."
        kpis={
          <>
            <Kpi titulo="Vendido" valor={dinero(r?.total ?? 0)} pie="lo confirmado" />
            <Kpi titulo="Ventas confirmadas" valor={numero(r?.confirmadas ?? 0)} pie={`${numero(r?.piezas ?? 0)} pieza${r?.piezas === 1 ? '' : 's'}`} />
            <Kpi
              titulo="En borrador"
              valor={numero(r?.enBorrador ?? 0)}
              pie={(r?.enBorrador ?? 0) > 0 ? 'no descontaron: avísale a quien supervisa' : 'ninguna pendiente'}
              tono={(r?.enBorrador ?? 0) > 0 ? 'malo' : 'normal'}
              bajarEsBueno
            />
          </>
        }
      />

      <Superfiltro
        buscar={filtros.buscar}
        onBuscar={(v) => poner('buscar', v)}
        placeholder="Buscar por número, cliente o pieza…"
        facetas={facetas}
        valores={{ ...filtros, periodo: periodo === 'hoy' ? undefined : periodo }}
        onCambiar={ponerVarios}
        onLimpiar={() => ponerVarios(sinFiltros(facetas))}
        resultados={isLoading ? undefined : textoDeResultados(ventas.length, todas.length, ['venta', 'ventas'])}
      />

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : isError ? (
        <div className="rounded-2xl border border-surface-border bg-surface p-6 text-center">
          <p className="text-sm text-slate-600 dark:text-slate-300">No se pudieron cargar tus ventas.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      ) : todas.length > 0 && ventas.length === 0 ? (
        <p className="rounded-lg border border-dashed border-surface-border px-4 py-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
          Ninguna venta coincide con la búsqueda o los filtros.
        </p>
      ) : ventas.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-surface-border p-8 text-center">
          <ShoppingBag className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-2 font-medium text-slate-800 dark:text-slate-100">Todavía no has pasado ventas en este período</p>
          <p className="mt-1 text-sm text-slate-500">Cuando cierres con un cliente, usa «Pasar venta» dentro de su chat.</p>
          <Link to="/conversations" className="mt-3 inline-flex min-h-[40px] items-center gap-2 rounded-full border border-surface-border px-4 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800">
            <MessagesSquare className="h-4 w-4" /> Ir a mis chats
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {ventas.map((v) => (
            <li key={v.id} data-venta={v.numero} className="rounded-2xl border border-surface-border bg-surface p-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">{v.numero}</span>
                <Badge variant={ESTADO[v.estado].tono}>{ESTADO[v.estado].texto}</Badge>
                <span className="text-sm text-slate-600 dark:text-slate-300">{v.cliente?.nombre ?? 'Cliente sin nombre'}</span>
                <span className="ml-auto text-base font-semibold tabular-nums text-slate-900 dark:text-slate-100">{dinero(v.total)}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {v.lineas.map((l) => `${l.cantidad} × ${l.productVariant.product.name} (${l.productVariant.label})`).join(' · ')}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span>{fecha(v.createdAt, true)}</span>
                {v.formaDePago && <span>· {v.formaDePago}</span>}
                <span className="ml-auto flex flex-wrap gap-2">
                  {v.conversationId && (
                    <Link to={`/conversations/${v.conversationId}`} className="inline-flex min-h-[34px] items-center gap-1.5 rounded-full border border-surface-border px-3 text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800">
                      <MessagesSquare className="h-3.5 w-3.5" /> Abrir el chat
                    </Link>
                  )}
                  {v.estado === 'CONFIRMADA' && (
                    <Button variant="outline" size="sm" onClick={() => setComprobante(v.id)}>
                      <Receipt className="h-3.5 w-3.5" /> Comprobante
                    </Button>
                  )}
                </span>
              </div>
              {v.estado === 'BORRADOR' && (
                <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  Quedó en borrador: no descontó existencia. La confirma quien supervisa, en Ventas.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {comprobante && (
        // La venta ya está aquí, completa y sin costos: el comprobante no vuelve a pedirla (la ruta
        // del chat exige que el chat siga siendo de ella, y una venta suya lo sigue siendo aunque
        // el chat haya pasado a otra persona).
        <Comprobante
          ventaId={comprobante}
          leer={async (id) => todas.find((v) => v.id === id) ?? Promise.reject(new Error('Esa venta ya no está en la lista'))}
          onCerrar={() => setComprobante(null)}
        />
      )}
    </div>
  );
}
